import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import LoadingView from "./LoadingView";
import { Heading } from "../design/heading";
import { Text } from "../design/text";
import { Button } from "../design/button";

interface RoleBasedProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: ("owner" | "admin" | "supervisor" | "parent")[];
  redirectPath?: string;
}

/**
 * A unified protected route that:
 * 1. Requires authentication
 * 2. Optionally restricts to specific roles
 * 3. Shows appropriate error messages
 */
const RoleBasedProtectedRoute: React.FC<RoleBasedProtectedRouteProps> = ({
  children,
  allowedRoles,
  redirectPath = "/login",
}) => {
  const { user, loading, role, activeRole, claims, refreshClaims } = useAuth();
  const isDebugMode = process.env.REACT_APP_DEBUG_MODE === "true" || process.env.NODE_ENV === "development";

  if (loading) {
    return <LoadingView />;
  }

  if (!user) {
    return <Navigate to={redirectPath} replace />;
  }

  // If no role restrictions, allow any authenticated user
  if (!allowedRoles) {
    return <>{children}</>;
  }

  const effectiveRole = activeRole || role;
  const hasAllowedRole = effectiveRole && allowedRoles.includes(effectiveRole as "admin" | "supervisor" | "parent");

  if (!hasAllowedRole) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8">
        <Heading>Access Denied</Heading>
        <Text className="text-center text-zinc-600 dark:text-zinc-400">
          You don't have permission to access this page.
        </Text>

        {isDebugMode && (
          <div className="p-4 mt-4 font-mono text-xs border rounded-lg border-amber-300 bg-amber-50 dark:bg-amber-900/20">
            <p className="font-bold text-amber-700">DEBUG INFO:</p>
            <p>User ID: {user?.uid}</p>
            <p>Email: {user?.email || "N/A"}</p>
            <p>Phone: {user?.phoneNumber || "N/A"}</p>
            <p>Claims Role: {role || "null"}</p>
            <p>Active Role: {activeRole || "null"}</p>
            <p>Effective Role: {effectiveRole || "null"}</p>
            <p>Required Roles: {allowedRoles.join(", ")}</p>
            <p className="mt-2">Claims:</p>
            <pre className="p-2 mt-1 overflow-auto text-xs rounded max-h-32 bg-amber-100 dark:bg-amber-800">
              {JSON.stringify(claims, null, 2)}
            </pre>
            <Button className="mt-4" onClick={() => refreshClaims()}>
              Refresh Claims
            </Button>
          </div>
        )}

        <a href="/login" className="text-blue-600 hover:underline">
          Sign in with a different account
        </a>
      </div>
    );
  }

  return <>{children}</>;
};

export default RoleBasedProtectedRoute;
